import { AxiosError } from 'axios';
import { motion } from 'framer-motion';
import { useAtom } from 'jotai';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useMutation } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';

import { createLicense } from '@/api/license/createLicense';
import { deleteLicense } from '@/api/license/deleteLicense';
import { deleteLicenseByProduct } from '@/api/license/deleteLicenseByProduct';
import { updateLicense } from '@/api/license/updateLicense';
import RoundBadge from '@/components/Common/Badge/RoundBadge';
import BasicButton from '@/components/Common/Button/BasicButton';
import DualFooterButton from '@/components/Common/Button/DualFooterButton';
import CarPriceTrendInfo from '@/components/Common/CarPriceTrendInfo';
import RadioList, { OptionType } from '@/components/Common/Input/Radio/RadioList';
import SimpleInput from '@/components/Common/Input/SimpleInput';
import TextInputLabelUp from '@/components/Common/Input/TextInputLabelUp';
import AlertModal from '@/components/Common/Modal/AlertModal';
import ConfirmModal from '@/components/Common/Modal/ConfirmModal';
import BottomSheetPopup from '@/components/Common/Popup/BottomSheetPopup';
import MenuHeader from '@/components/Header/MenuHeader';
import { CheckOutlineIcon, ChevronRightIcon } from '@/components/Icon';
import ConfirmExitPopup from '@/components/Products/ConfirmExitPopup';
import SimilarProductList from '@/components/Products/SimilarProductList';
import { colors } from '@/const/colors';
import { COMMON_TOAST_ERROR } from '@/const/errorMessage';
import { BASE_TONNAGE, GENERAL_CARGO, INDIVIDUAL } from '@/const/license';
import { BEFORE_SALE, PAUSE, SALE } from '@/const/productStatus';
import { SALESTYPE } from '@/const/products';
import { SELL_CAR_DEFAULT_TITLE } from '@/const/title';
import { useToastContext } from '@/contexts/Common/ToastContext';
import { useLicenseEnumContext } from '@/contexts/License/LicenseEnumContext';
import { useMemberContext } from '@/contexts/Member/MemberContext';
import useUpdateProductFormMutation from '@/hooks/query/useUpdateProductFormMutation';
import useBlockNavigation from '@/hooks/useBlockNavigation';
import useExitConfirmationPopup from '@/hooks/useExitConfirmationPopup';
import useFetchProductData from '@/hooks/useFetchProductData';
import DefaultHelmet from '@/metadatas/DefaultHelmet';
import { initialLicenseDraft, licenseDraftAtom, productsFormAtom, useResetProductsForm } from '@/store/products';
import { determineLicensePlateDescription } from '@/utils/Products/productUtils';
import { formatNumber, requiresSaleAuthorityDocuments } from '@/utils/common';
import { getLicenseTypeValue } from '@/utils/license/licenseUtils';
import { validatePrice } from '@/utils/validation';

type SalesTypeKey = keyof typeof SALESTYPE;

interface LicenseData {
  id?: number;
  productId: number;
  licenseType: EnumPresenter;
  price: number;
  maxTons: number;
  tons: number;
  // locate: EnumPresenter;
  isSaleLicense?: boolean;
}

const PriceInputForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showToast } = useToastContext();
  const { member } = useMemberContext();
  const { licenseEnum } = useLicenseEnumContext();

  const [productFormData, setProductFormData] = useAtom(productsFormAtom);
  const [licenseDraft, setLicenseDraft] = useAtom(licenseDraftAtom);
  const resetProductsForm = useResetProductsForm();

  const [isExitWithoutSavingLicense, setIsExitWithoutSavingLicense] = useState(false);
  const [isShowLicenseSection, setIsShowLicenseSection] = useState(false);
  const [descriptionOfMaxTons, setDescriptionOfMaxTons] = useState(BASE_TONNAGE);
  const [prevData, setPrevData] = useState<ProductDetailResponse | null>(null);

  const productSearchParams = useFetchProductData(id, true);

  const productStatusRef = useRef<string>();
  const isRegisterSubmittingRef = useRef(false);
  const isRetryingPostLicenseCreateRef = useRef(false);
  const priceRef = useRef<HTMLInputElement>(null);
  const [priceError, setPriceError] = useState(false);
  const [priceErrorMsg, setPriceErrorMsg] = useState('');

  const [isOpenLicenseInfo, setIsOpenLicenseInfo] = useState(false);
  const [isOpenOptionBottomSheet, setIsOpenOptionBottomSheet] = useState(false);
  const [title, setTitle] = useState('');
  const [optionData, setOptionData] = useState<OptionType[]>([]);
  const [isOpenAlertModal, setIsOpenAlertModal] = useState(false);
  const [alertModalConfig, setAlertModalConfig] = useState<ModalConfig>({
    title: '',
    content: null,
    rightButtonLabel: '',
    onClose: () => {},
  });

  const [licenseData, setLicenseData] = useState<LicenseData | null>(null); // 팝업 내 폼 편집용
  const [isClickedSave, setIsClickedSave] = useState(false);
  const [isRegisterSubmitting, setIsRegisterSubmitting] = useState(false);

  const resetRegisterSubmitting = () => {
    isRegisterSubmittingRef.current = false;
    setIsRegisterSubmitting(false);
  };

  const beginRegisterSubmitting = () => {
    isRegisterSubmittingRef.current = true;
    setIsRegisterSubmitting(true);
  };

  const isLicenseIncludeSale = licenseDraft.isLicenseIncludeSale ?? productFormData?.isSaleLicense ?? null;
  const isLicenseInfoFilled = !!licenseDraft.license?.licenseType?.code && !!licenseDraft.license?.price;

  // 회원 실명이 소유주명과 다르고 현물출자(위수탁) 이력에도 없으면 판매 권한 증빙 서류 제출이 필요하다.
  const requiresSaleAuthorityDocs = requiresSaleAuthorityDocuments(
    member?.realName,
    productFormData?.ownerName,
    productFormData?.lastOwnerInfo?.content,
  );

  const popupTitle = {
    licenseType: '번호판 종류',
    // locate: '지역',
  };

  const handleFocusPriceInput = () => {
    setIsShowLicenseSection(true);
    scrollIntoView();
  };

  const handleChangePrice = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const { isValid, errorMessage, formattedValue } = validatePrice(value);

    setProductFormData({
      ...productFormData,
      price: formattedValue,
    } as ProductDetailResponse);

    if (!isValid) {
      setPriceError(true);
      setPriceErrorMsg(errorMessage);
    } else {
      setPriceError(false);
      setPriceErrorMsg('');
    }
  };

  const scrollIntoView = () => {
    if (priceRef.current) {
      priceRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleGoPrev = () => {
    navigate(`/products/sales/photo/${id}`, { replace: true });
  };

  const callVehiclePatch = () => {
    // 소유주와 인증 이름이 다르면 판매 등록을 완료하기 전에 증빙 서류 첨부 단계로 이동한다.
    if (requiresSaleAuthorityDocs) {
      goToDocumentMutation.mutate(
        {
          id: Number(productFormData?.id),
          price: Number(productFormData?.price),
          isSaleLicense: !!isLicenseIncludeSale,
          status: BEFORE_SALE,
        },
        {
          onError: () => {
            resetRegisterSubmitting();
          },
        },
      );
      return;
    }

    const isDealerType = member?.type?.code === 'DEALER' ? true : false;
    const request = {
      id: Number(productFormData?.id),
      price: Number(productFormData?.price),
      isSaleLicense: !!isLicenseIncludeSale,
      status: isDealerType ? PAUSE : SALE,
    };
    updateProductNextStepMutation.mutate(request, {
      onError: () => {
        resetRegisterSubmitting();
      },
    });
  };

  const handleFinishRegisterForm = async () => {
    if (isRegisterSubmittingRef.current) return;

    const licenseToUse = licenseDraft.license ?? licenseData;

    let isValid = false;
    if (isLicenseIncludeSale === false) {
      isValid = true;
    } else {
      isValid = !!licenseToUse?.licenseType && !!licenseToUse?.price;
    }

    if (isValid) {
      beginRegisterSubmitting();

      if (isLicenseIncludeSale === false) {
        try {
          await deleteLicenseByProduct({ productId: Number(productFormData.id) });
        } catch {
          showToast(COMMON_TOAST_ERROR, 'error', 'bottom');
          resetRegisterSubmitting();
          return;
        }

        callVehiclePatch();
        return;
      }

      if (isLicenseIncludeSale && licenseToUse) {
        // 번호판 API 먼저 호출 후, 성공 시 차량 patch 호출
        const licenseRequest = {
          licenseType: licenseToUse.licenseType?.code,
          maxTons: licenseToUse.maxTons || BASE_TONNAGE,
          price: licenseToUse.price,
          productId: productFormData.id,
          memberId: member?.id,
        };

        if (licenseToUse.id) {
          patchLicenseMutation.mutate(
            { ...licenseRequest, id: licenseToUse.id },
            {
              onSuccess: () => {
                callVehiclePatch();
              },
              onError: () => {
                resetRegisterSubmitting();
              },
            },
          );
        } else {
          postLicenseMutation.mutate(licenseRequest, {
            onSuccess: () => {
              callVehiclePatch();
            },
          });
        }
      } else {
        callVehiclePatch();
      }
    } else if (isLicenseIncludeSale === null) {
      setIsOpenAlertModal(true);
      setAlertModalConfig({
        title: '번호판 판매 유무를 선택해주세요',
        content: (
          <>
            번호판 판매 유무를 선택해야
            <br />
            차량 판매 등록을 완료할 수 있어요.
          </>
        ),
        rightButtonLabel: '선택하기',
        onClose: () => {
          setIsOpenAlertModal(false);
        },
      });
    } else {
      setIsOpenAlertModal(true);
      setAlertModalConfig({
        title: '번호판 정보를 입력해주세요',
        content: (
          <>
            번호판 정보를 입력해야
            <br />
            판매 등록이 가능해요.
          </>
        ),
        rightButtonLabel: '정보 입력하기',
        onClose: () => {
          setIsOpenAlertModal(false);
          setIsOpenLicenseInfo(true);
        },
      });
    }
  };

  const handleClickIncludeLicensePlate = (isInclude: boolean) => {
    const newValue = isInclude;

    if (newValue === false) {
      setLicenseDraft({
        ...initialLicenseDraft,
        isLicenseIncludeSale: false,
      });
      setLicenseData(null);
    } else {
      setLicenseDraft((prev) => ({ ...prev, isLicenseIncludeSale: newValue }));
      if (newValue === true && !licenseDraft.license) {
        setIsOpenLicenseInfo(true);
      }
    }
  };

  const handleClickOpenBottomSheet = () => {
    const draftLicense = licenseDraft.license;
    if (draftLicense) {
      setLicenseData({ ...draftLicense, productId: productFormData?.id } as LicenseData);
    } else {
      setLicenseData({
        productId: productFormData?.id,
        licenseType: undefined as unknown as EnumPresenter,
        price: undefined as unknown as number,
        maxTons: BASE_TONNAGE,
        tons: 0,
      } as LicenseData);
    }
    setIsOpenLicenseInfo(true);
  };

  const handleOpenOption = (key: 'licenseType') => {
    const arr: OptionDataType[] = [];
    setTitle(key);

    if (key === 'licenseType') {
      const newOptionData = licenseEnum.licenseType.map((item) => {
        // if (item?.code === INDIVIDUAL) {
        //   return {
        //     ...item,
        //     desc: `${item.desc} (1.5톤 이상 ~ ${descriptionOfMaxTons}톤)`,
        //   };
        // }
        return item;
      });

      setOptionData(newOptionData);
    }
    setIsOpenOptionBottomSheet(true);
  };

  const handleChangeLicensePrice = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let price = String(value).replace(/[^0-9.]/g, '');
    if (!value) {
      setLicenseData({
        ...licenseData,
        [name]: undefined,
      } as LicenseData);
    } else if (/^[0-9]*$/.test(price) || price === '0') {
      if (Number(price) >= 100000) {
        setLicenseData({
          ...licenseData,
          [name]: 99999,
        } as LicenseData);
      } else {
        setLicenseData({
          ...licenseData,
          [name]: Number(price),
        } as LicenseData);
      }
    }
  };

  const checkForPriceChanges = () => {
    return (
      (!!productFormData.price && prevData?.price !== productFormData.price) ||
      prevData?.isLicenseIncludeSale !== isLicenseIncludeSale
    );
  };

  const updateProductTemporarySaveMutation = useUpdateProductFormMutation({});

  const deleteLicenseMutation = useMutation(
    (request: { id: number; isSoftDelete: boolean }) => deleteLicense(request),
    {
      onSuccess: () => {
        setLicenseDraft(initialLicenseDraft);
        setLicenseData(null);
        saveProductChanges();
      },
    },
  );

  const saveProductChanges = () => {
    const request = {
      id: Number(productFormData?.id),
      price: productFormData?.price ? Number(productFormData?.price) : null,
      status: 'BEFORE_SALE',
      isSaleLicense: isLicenseIncludeSale !== null ? isLicenseIncludeSale : null,
    };
    updateProductTemporarySaveMutation.mutate(request);
  };

  const saveChangesData = () => {
    if (!isLicenseIncludeSale && licenseDraft.license?.id) {
      deleteLicenseMutation.mutate({ id: licenseDraft.license.id, isSoftDelete: false });
    } else {
      saveProductChanges();
    }
  };

  const { isShowExitConfirmPopup, openExitConfirmPopup, closeExitConfirmPopup, confirmExit } = useExitConfirmationPopup(
    checkForPriceChanges,
    saveChangesData,
    resetProductsForm,
  );

  useBlockNavigation(openExitConfirmPopup);

  const updateProductNextStepMutation = useUpdateProductFormMutation({
    url: `/products/${id}`,
    isFinalStep: true,
    onInvalidResponse: resetRegisterSubmitting,
  });

  // 증빙 서류 제출이 필요한 경우, 현재까지 입력한 값을 임시 저장하고 서류 첨부 페이지로 이동한다.
  const goToDocumentMutation = useUpdateProductFormMutation({
    url: `/products/sales/document/${id}`,
    onInvalidResponse: resetRegisterSubmitting,
  });

  useLayoutEffect(() => {
    const detectMobileKeyboard = () => scrollIntoView();
    window.addEventListener('resize', detectMobileKeyboard);
    return () => {
      window.removeEventListener('resize', detectMobileKeyboard);
    };
  }, []);

  const handleCloseOptionBottomSheet = () => {
    setIsOpenOptionBottomSheet(false);
  };

  const handleExitLicenseInfo = () => {
    setIsExitWithoutSavingLicense(false);
    setIsOpenLicenseInfo(false);
    const draftLicense = licenseDraft.license;
    if (draftLicense) {
      setLicenseData({
        ...draftLicense,
        productId: productFormData?.id,
      } as LicenseData);
    } else {
      setLicenseData(null);
    }
  };

  const handleCloseLiceseInfo = () => {
    setIsOpenLicenseInfo(false);
    setIsClickedSave(false); // 다음에 바텀시트 열었을 때 canClose 로직 정상 동작하도록 리셋
  };

  const onClickItem = (item: OptionDataType, key: string) => {
    setLicenseData({ ...licenseData, [key]: item } as LicenseData);
    setTimeout(() => {
      setIsOpenOptionBottomSheet(false);
    }, 200);
  };

  const onClickSaveLiceseInfo = () => {
    if (!licenseData) return;
    setLicenseDraft((prev) => ({
      ...prev,
      license: {
        id: licenseData.id,
        productId: licenseData.productId,
        licenseType: licenseData.licenseType,
        price: licenseData.price,
        maxTons: licenseData.maxTons ?? BASE_TONNAGE,
        tons: licenseData.tons ?? 0,
      },
      isLicenseIncludeSale: true,
    }));
    setIsClickedSave(true);
    setIsOpenLicenseInfo(false);
  };

  const postLicenseMutation = useMutation((request: LicenseRequest) => createLicense(request), {
    onSuccess: (res) => {
      showToast('번호판 정보가 저장되었어요.', 'success', 'bottom');
      setIsOpenLicenseInfo(false);
      setLicenseData(res.data);
      setProductFormData((prev) => ({ ...prev, license: res.data }));
      setTimeout(() => setIsClickedSave(false), 100);
    },
    onError: async (error: AxiosError, variables: LicenseRequest) => {
      const errorData = (error as any)?.response?.data ?? (error as any)?.data ?? error;
      const code = typeof errorData === 'object' ? (errorData as any)?.code : undefined;
      const message = typeof errorData === 'string' ? errorData : (errorData as { message?: string })?.message;

      // 이미 번호판이 등록되어 연결된 경우:
      // 1) productId 기준 기존 연결을 삭제
      // 2) 동일 request로 다시 생성
      if (
        code === 'ALREADY_SALE_LICENSE_WITH_PRODUCTS' &&
        !isRetryingPostLicenseCreateRef.current &&
        variables?.productId
      ) {
        isRetryingPostLicenseCreateRef.current = true;
        try {
          const productId = Number(variables.productId);
          await deleteLicenseByProduct({ productId });

          const res = await createLicense(variables);
          showToast('번호판 정보가 저장되었어요.', 'success', 'bottom');
          setIsOpenLicenseInfo(false);
          setLicenseData(res.data);
          setProductFormData((prev) => ({ ...prev, license: res.data }));
          setTimeout(() => setIsClickedSave(false), 100);
          callVehiclePatch();
        } catch (retryError: any) {
          const retryErrorData = retryError?.response?.data ?? retryError?.data ?? retryError;
          const retryMessage =
            typeof retryErrorData === 'string' ? retryErrorData : (retryErrorData as { message?: string })?.message;

          showToast(retryMessage || COMMON_TOAST_ERROR, 'error', 'bottom');
          setTimeout(() => setIsClickedSave(false), 100);
          resetRegisterSubmitting();
        } finally {
          isRetryingPostLicenseCreateRef.current = false;
        }
        return;
      }

      if (message) {
        showToast(message, 'error', 'bottom');
      } else {
        showToast(COMMON_TOAST_ERROR, 'error', 'bottom');
      }
      setTimeout(() => setIsClickedSave(false), 100);
      resetRegisterSubmitting();
    },
  });

  const patchLicenseMutation = useMutation((request: LicenseRequest) => updateLicense(request), {
    onSuccess: (res) => {
      showToast('번호판 정보가 수정되었어요.', 'success', 'bottom');
      setIsOpenLicenseInfo(false);
      setLicenseData(res.data);
      setProductFormData((prev) => ({ ...prev, license: res.data }));
      setTimeout(() => setIsClickedSave(false), 100);
    },
    onError: () => {
      showToast(COMMON_TOAST_ERROR, 'error', 'bottom');
      setTimeout(() => {
        setIsClickedSave(false);
      }, 100);
      resetRegisterSubmitting();
    },
  });

  const isInvalidLicenseData = (licenseData?: LicenseData | null) => {
    if (!licenseData) return true;

    const { licenseType, price } = licenseData;

    if (licenseType?.code === GENERAL_CARGO) {
      return !licenseType || !price;
    }

    return !licenseType || !price;
  };

  const validatePopupClose = () => {
    const draftLicense = licenseDraft.license;
    const isNotEdit =
      draftLicense?.id === licenseData?.id &&
      draftLicense?.licenseType?.code === licenseData?.licenseType?.code &&
      draftLicense?.price === licenseData?.price;

    // if (!isNotEdit) {
    //   setIsExitWithoutSavingLicense(true);
    // }
    return isNotEdit;
  };

  useEffect(() => {
    productStatusRef.current = productFormData?.status.code;
  }, [productFormData]);

  useEffect(() => {
    setPrevData(productFormData);
    setIsShowLicenseSection(
      productFormData?.isSaleLicense !== null || licenseDraft.isLicenseIncludeSale !== null || !!productFormData?.price,
    );
    setDescriptionOfMaxTons(determineLicensePlateDescription(Number(productFormData?.tons)));

    // API에서 불러온 기존 번호판이 있고 draft가 비어있으면 draft에 채움
    const apiLicense = productFormData?.license;
    if (
      !licenseDraft.license &&
      licenseDraft.isLicenseIncludeSale === null &&
      apiLicense?.licenseType &&
      apiLicense?.price
    ) {
      setLicenseDraft({
        license: {
          id: apiLicense.id,
          productId: productFormData.id,
          licenseType: apiLicense.licenseType,
          price: apiLicense.price,
          maxTons: apiLicense.maxTons ? Number(apiLicense.maxTons) : 16,
          tons: apiLicense.tons ? Number(apiLicense.tons) : 0,
        },
        isLicenseIncludeSale: productFormData?.isSaleLicense ?? true,
      });
    }
  }, [productFormData.id, productFormData?.license, licenseDraft.license, licenseDraft.isLicenseIncludeSale]);

  return (
    <>
      <DefaultHelmet page="sale" />

      <MenuHeader title={SALESTYPE[productFormData.type?.code as SalesTypeKey] || SELL_CAR_DEFAULT_TITLE} />
      <div className="flex flex-col w-full p-4 pb-24 pt-[60px]">
        <div className="py-4 pb-24 my-2">
          <div className="flex justify-between pb-[30px]">
            <h3 className="text-2xl font-bold text-gray-8 break-keep">판매 금액을 입력해주세요.</h3>
            <RoundBadge text="9/9" />
          </div>
          <div className="border border-gray-4 rounded-[8px] p-[18px]">
            <TextInputLabelUp
              ref={priceRef}
              name="price"
              label="차량 판매 금액"
              placeholder="차량 판매 금액 입력"
              value={(productFormData?.price !== 0 && formatNumber(productFormData?.price?.toString(), true)) || ''}
              onChange={handleChangePrice}
              onFocus={handleFocusPriceInput}
              inputMode="numeric"
              error={priceError}
              errorMsg={priceErrorMsg}
              suffix="만원"
              maxLength={8}
            />
            <p className={`text-gray-7 text-[13px] leading-[17px] mt-[6px] ${!!priceError ? 'invisible' : ''}`}>
              * 부가세, 번호판 금액은 제외하고 입력해주세요.
            </p>
          </div>
          <motion.div
            className="bg-gray-1 rounded-[8px] p-[18px] mt-3"
            initial={{ height: 'auto', opacity: 1 }}
            animate={{ height: 'auto', opacity: 1 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            style={{ overflow: 'hidden' }}
          >
            <div
              className={`mb-2 font-medium text-gray-8 sm280:relative ${
                !!isLicenseIncludeSale ? 'sm280:mt-[30px]' : ''
              }`}
            >
              혹시 번호판도 판매하시나요?
              {!!isLicenseIncludeSale && (
                <span
                  className={`sm280:absolute sm280:top-[-30px] sm280:left-[-8px] px-2 py-1 w-auto  rounded-[17px] ml-2 text-[12px] ${
                    isLicenseInfoFilled ? 'bg-blue-5 text-blue-3' : 'bg-gray-3 text-gray-6'
                  }`}
                >
                  {isLicenseInfoFilled ? '입력 완료' : '입력전'}
                </span>
              )}
            </div>
            <div className="text-gray-7 text-[12px] leading-[15px] mb-[14px]">
              간단한 번호판 정보를 알려주시면 차량 판매완료 시점에 <br className="sm280:hidden sm680:hidden" />
              번호판도 판매하실 수 있도록 도와드릴게요.
            </div>
            <div className="flex flex-col gap-2">
              <BasicButton
                name="아니요, 괜찮아요"
                icon={<CheckOutlineIcon color={isLicenseIncludeSale === false ? colors.primary[10] : colors.gray[5]} />}
                bgColor={isLicenseIncludeSale === false ? colors.blue[5] : colors.gray[3]}
                borderColor={isLicenseIncludeSale === false ? colors.primary[10] : colors.gray[3]}
                textColor={isLicenseIncludeSale === false ? colors.primary[10] : colors.gray[8]}
                fontSize={14}
                height={44}
                fontWeight="normal"
                onClick={() => handleClickIncludeLicensePlate(false)}
              />
              <BasicButton
                name="네, 같이 판매할게요"
                icon={<CheckOutlineIcon color={!!isLicenseIncludeSale ? colors.primary[10] : colors.gray[5]} />}
                bgColor={!!isLicenseIncludeSale ? colors.blue[5] : colors.gray[3]}
                borderColor={!!isLicenseIncludeSale ? colors.primary[10] : colors.gray[3]}
                textColor={!!isLicenseIncludeSale ? colors.primary[10] : colors.gray[8]}
                fontSize={14}
                height={44}
                fontWeight="normal"
                onClick={() => handleClickIncludeLicensePlate(true)}
              />
            </div>
            {!!isLicenseIncludeSale && (
              <div
                className="underline text-gray-8 underline-offset-4 text-[14px] text-center mt-[14px]"
                onClick={handleClickOpenBottomSheet}
              >
                번호판 정보 {!!licenseDraft.license?.id ? '수정' : '입력'}하기
              </div>
            )}
          </motion.div>

          <div>
            <CarPriceTrendInfo
              priceSearchParams={productSearchParams}
              showPriceComparison={false}
              apiType="private"
              pageType="priceForm"
            />
            <SimilarProductList similarSearchParams={productSearchParams} />
          </div>
          <DualFooterButton
            leftButtonText="이전"
            onClickLeftButton={handleGoPrev}
            rightButtonText={requiresSaleAuthorityDocs ? '다음' : '등록완료'}
            disabledRight={
              !productFormData.price ||
              isRegisterSubmitting ||
              updateProductNextStepMutation.isLoading ||
              goToDocumentMutation.isLoading ||
              patchLicenseMutation.isLoading ||
              postLicenseMutation.isLoading
            }
            onClickRightButton={handleFinishRegisterForm}
          />
        </div>
      </div>
      <ConfirmExitPopup isShow={isShowExitConfirmPopup} onClose={closeExitConfirmPopup} onConfirm={confirmExit} />
      <BottomSheetPopup
        isOpen={isOpenLicenseInfo}
        onClose={handleCloseLiceseInfo}
        title="번호판 정보"
        canClose={!isClickedSave ? validatePopupClose : () => true}
      >
        <div className="h-full px-4 pb-2">
          <dl className="flex items-center justify-center py-5 border-b border-gray-3">
            <dt className="min-w-[100px] text-gray-7">번호판 종류</dt>
            <dd
              className="flex items-center justify-between flex-1 px-3 text-gray-8"
              onClick={() => handleOpenOption('licenseType')}
            >
              <SimpleInput
                name="fueling"
                placeholder="번호판 종류 선택"
                value={getLicenseTypeValue(licenseData?.licenseType, descriptionOfMaxTons)}
                readOnly
              ></SimpleInput>
              <ChevronRightIcon color={colors.gray[6]} />
            </dd>
          </dl>
          {/* {licenseData?.licenseType?.code === GENERAL_CARGO && (
            <dl className="flex items-center justify-center py-5 border-b border-gray-3">
              <dt className="min-w-[100px] text-gray-7">지역</dt>
              <dd
                className="flex items-center justify-between flex-1 px-3 text-gray-8"
                onClick={() => handleOpenOption('locate')}
              >
                <SimpleInput
                  name="fueling"
                  placeholder="지역 선택"
                  value={licenseData?.locate?.desc || ''}
                  readOnly
                ></SimpleInput>
                <ChevronRightIcon color={colors.gray[6]} />
              </dd>
            </dl>
          )} */}

          <dl className="flex items-center justify-center py-5 mb-4 border-b border-gray-3">
            <dt className="min-w-[100px] text-gray-7">
              <span className="align-middle">번호판 금액</span>
            </dt>
            <dd className="flex justify-between flex-1 px-3 text-gray-8">
              <SimpleInput
                placeholder="번호판 금액 입력"
                name="price"
                value={formatNumber(licenseData?.price?.toString(), true) || ''}
                onChange={(e) => {
                  handleChangeLicensePrice(e);
                }}
                inputMode="numeric"
              ></SimpleInput>
              <span className="text-gray-8 w-[60px]">만원</span>
            </dd>
          </dl>

          <BasicButton
            name="저장"
            bgColor={colors.primary[10]}
            borderColor={colors.primary[10]}
            textColor={colors.gray[0]}
            fontSize={16}
            height={48}
            fontWeight="bold"
            isDisabled={isInvalidLicenseData(licenseData)}
            onClick={onClickSaveLiceseInfo}
          ></BasicButton>
        </div>
      </BottomSheetPopup>

      <BottomSheetPopup
        isOpen={isOpenOptionBottomSheet}
        onClose={handleCloseOptionBottomSheet}
        title={`${popupTitle[title as 'licenseType']}`}
        isBackdrop={false}
        isXMarkVisible={false}
        isPrevBtnVisible={true}
      >
        <div className="px-4 pb-8">
          <RadioList
            name="license"
            list={optionData}
            value={(licenseData && licenseData[title as 'licenseType']?.code) || ''}
            onChange={(val) => {
              onClickItem(val, title);
            }}
            horizontal={false}
          ></RadioList>
        </div>
      </BottomSheetPopup>

      <ConfirmModal
        isOpen={isExitWithoutSavingLicense}
        title={`<span class="font-medium">번호판 정보 입력 취소</span>`}
        content={
          <span>
            번호판 정보를 저장하지 않고
            <br />
            나가시겠어요?
          </span>
        }
        leftButtonLabel="아니오"
        onLeftButtonClick={() => {
          setIsExitWithoutSavingLicense(false);
        }}
        rightButtonLabel="나가기"
        onRightButtonClick={handleExitLicenseInfo}
      />
      <AlertModal
        isOpen={isOpenAlertModal}
        title={alertModalConfig.title}
        content={alertModalConfig.content}
        buttonLabel={alertModalConfig.rightButtonLabel}
        onClose={alertModalConfig.onClose}
      />
    </>
  );
};

export default PriceInputForm;
